const DEFAULT_DURATION_MS = 320;
const DEFAULT_OFFSET_PX = 20;

interface SmoothScrollIntoViewOptions {
  block?: ScrollLogicalPosition;
  behavior?: ScrollBehavior;
  offset?: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function easeOutCubic(value: number): number {
  return 1 - Math.pow(1 - value, 3);
}

function getScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;

  while (current) {
    const styles = window.getComputedStyle(current);
    const overflowY = styles.overflowY;
    const isScrollable = /(auto|scroll|overlay)/.test(overflowY);

    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

function getWindowTargetTop(
  target: HTMLElement,
  block: ScrollLogicalPosition,
  offset: number,
): number {
  const rect = target.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const currentTop = window.scrollY;

  if (block === 'center') {
    return currentTop + rect.top - (viewportHeight - rect.height) / 2;
  }

  return currentTop + rect.top - offset;
}

function getContainerTargetTop(
  container: HTMLElement,
  target: HTMLElement,
  block: ScrollLogicalPosition,
  offset: number,
): number {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const relativeTop = targetRect.top - containerRect.top;

  if (block === 'center') {
    return container.scrollTop + relativeTop - (container.clientHeight - targetRect.height) / 2;
  }

  return container.scrollTop + relativeTop - offset;
}

/**
 * Smoothly scrolls an element into view with a JS-driven ease-out animation.
 * This avoids the abrupt native `scrollIntoView` behavior seen on some mobile browsers.
 */
export function smoothScrollIntoView(
  target: HTMLElement,
  {
    block = 'center',
    behavior = 'smooth',
    offset = DEFAULT_OFFSET_PX,
  }: SmoothScrollIntoViewOptions = {},
): () => void {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shouldAnimate = behavior === 'smooth' && !reduceMotion;
  const scrollContainer = getScrollableAncestor(target);
  const maxScrollTop = scrollContainer
    ? Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight)
    : Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const startTop = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
  const desiredTop = scrollContainer
    ? getContainerTargetTop(scrollContainer, target, block, offset)
    : getWindowTargetTop(target, block, offset);
  const targetTop = clamp(desiredTop, 0, maxScrollTop);

  if (!shouldAnimate || Math.abs(targetTop - startTop) < 2) {
    if (scrollContainer) {
      scrollContainer.scrollTop = targetTop;
    } else {
      window.scrollTo({ top: targetTop, behavior: 'auto' });
    }

    return () => {};
  }

  const startTime = performance.now();
  let frameId = 0;

  const step = (timestamp: number) => {
    const progress = Math.min((timestamp - startTime) / DEFAULT_DURATION_MS, 1);
    const nextTop = startTop + (targetTop - startTop) * easeOutCubic(progress);

    if (scrollContainer) {
      scrollContainer.scrollTop = nextTop;
    } else {
      window.scrollTo({ top: nextTop, behavior: 'auto' });
    }

    if (progress < 1) {
      frameId = window.requestAnimationFrame(step);
    }
  };

  frameId = window.requestAnimationFrame(step);

  return () => {
    window.cancelAnimationFrame(frameId);
  };
}
